import { TextField } from '@mui/material';
import { TimePicker as MuiTimePicker, TimePickerProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useAppThemeContext } from '../../../contexts';

type TTimePickerProps = Omit<TimePickerProps<Dayjs, Dayjs>, 'renderInput'> & {
  label?: string;
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  fullWidth?: boolean;
}

export const TimePicker: React.FC<TTimePickerProps> = ({
  label,
  value,
  onChange,
  fullWidth = true,
  ...rest
}) => {
  const { isMobile } = useAppThemeContext();
  const [displayValue, setDisplayValue] = useState('');

  // Sync display value when external value changes
  useEffect(() => {
    if (!isMobile && value && dayjs.isDayjs(value) && value.isValid()) {
      setDisplayValue(value.format('HH:mm'));
    } else if (!isMobile && !value) {
      setDisplayValue('');
    }
  }, [value, isMobile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '').slice(0, 4);

    // Build formatted string progressively: "2" → "23" → "23:0" → "23:00"
    let formatted = digits;
    if (digits.length > 2) {
      formatted = digits.slice(0, 2) + ':' + digits.slice(2);
    }

    setDisplayValue(formatted);

    // Auto-parse when user has typed 4 digits
    if (digits.length === 4) {
      const h = parseInt(digits.slice(0, 2), 10);
      const m = parseInt(digits.slice(2), 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const newValue = dayjs().hour(h).minute(m).second(0).millisecond(0);
        onChange?.(newValue);
      }
    }
  };

  // Mobile: clock-only (readOnly)
  if (isMobile) {
    return (
      <MuiTimePicker
        {...rest}
        label={label}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth={fullWidth}
            inputProps={{
              ...params.inputProps,
              readOnly: true,
              placeholder: 'hh:mm'
            }}
          />
        )}
        minutesStep={15}
        ampm={false}
        value={value}
        onChange={onChange}
      />
    );
  }

  // Web: keyboard typing with auto-format "2300" → "23:00"
  return (
    <MuiTimePicker
      {...rest}
      label={label}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          value={displayValue}
          onChange={handleInputChange}
          inputProps={{
            ...params.inputProps,
            readOnly: false,
            placeholder: 'hh:mm'
          }}
        />
      )}
      minutesStep={15}
      ampm={false}
      value={value}
      onChange={onChange}
    />
  );
};
